import { Fragment, useEffect, useState } from 'react'
import { Plus, Trash2, Loader2, Languages, ChevronDown, Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useRegistryPerms } from '../lib/perms'
import { useConfirm, deleteJSON } from './ConfirmDialog'
import { apiError, okJSON } from '../lib/api'
import { stopReasonsCache } from '../lib/catalogs'
import { TEXT_LANGS, cleanTextMap } from '../lib/i18nText'
import AdminMaterialCatalog from './AdminMaterialCatalog'

const PLAN_CODES = ['PLAN-TON', 'PLAN-BMC', 'PLAN-DRILL-METERS']

const inputCls = 'text-sm border border-slate-200 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500'
const primaryBtn = 'inline-flex items-center gap-1.5 bg-brand text-white rounded-md px-3 py-1.5 text-sm font-semibold hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400'

function Chip({ label, color }) {
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: `${color}1a`, color }}>
      {label}
    </span>
  )
}

// Traduções do rótulo de um motivo de paragem. O PUT do backend troca o registo inteiro,
// então code, label e bucket vão junto, iguais aos da linha.
function StopReasonLabels({ reason, onSaved }) {
  const { t } = useTranslation()
  const { mayEdit, reasonEdit } = useRegistryPerms()
  const [labels, setLabels] = useState(() => Object.fromEntries(TEXT_LANGS.map(l => [l, reason.labels?.[l] || ''])))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)

  const save = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      await okJSON(`/api/stop-reasons/${reason.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: reason.code, label: reason.label, bucket: reason.bucket, labels: cleanTextMap(labels) }),
      })
      setSaved(true)
      onSaved()
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={save} className="py-3 space-y-2">
      <p className="text-[11px] text-slate-500 dark:text-slate-400">{t('admin:production.stops.labels_hint')}</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {TEXT_LANGS.map(l => (
          <label key={l} className="flex flex-col gap-1">
            <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">{l.toUpperCase()}</span>
            <input className={inputCls} value={labels[l]} disabled={!mayEdit || saving}
              placeholder={l === 'pt' ? reason.label : ''}
              onChange={e => { setSaved(false); setLabels(prev => ({ ...prev, [l]: e.target.value })) }} />
          </label>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button type="submit" disabled={!mayEdit || saving} title={!mayEdit ? t(reasonEdit) : undefined} className={primaryBtn}>
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {t('admin:production.stops.save_labels')}
        </button>
        {saved && <span className="text-xs text-status-op">{t('admin:production.stops.labels_saved')}</span>}
        {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
      </div>
    </form>
  )
}

const translatedCount = (labels) => Object.keys(cleanTextMap(labels)).length

// renderDetail (opcional) acrescenta um expansor por linha; onChanged roda depois de
// toda gravação que muda a lista.
function CrudSection({ title, subtitle, endpoint, columns, toRow, fields, buildBody, validate, renderDetail, detailToggle, onChanged }) {
  const { t } = useTranslation()
  const { mayEdit, mayDelete, reasonEdit, reasonDelete } = useRegistryPerms()
  const [openId, setOpenId] = useState(null)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(() => Object.fromEntries(fields.map(f => [f.name, f.default ?? ''])))
  const [confirmDialog, ask] = useConfirm()

  // quiet: recarga depois de gravar dentro de uma linha aberta; o spinner trocaria a
  // tabela inteira e desmontaria o expansor com a mensagem de sucesso.
  const load = async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true)
    setError(null)
    try {
      const res = await fetch(endpoint)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setItems(Array.isArray(data) ? data : [])
    } catch (err) {
      setError(err.message)
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const resetForm = () => setForm(Object.fromEntries(fields.map(f => [f.name, f.default ?? ''])))

  const add = async (e) => {
    e.preventDefault()
    if (validate && !validate(form)) return
    setSaving(true)
    setError(null)
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildBody(form)),
      })
      if (!res.ok) throw await apiError(res)
      resetForm()
      await load()
      onChanged?.()
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const remove = (item) => ask({
    title: t('common:actions.delete'),
    message: t('admin:production.delete_confirm'),
    confirmLabel: t('common:actions.delete'),
    danger: true,
    onConfirm: async () => { await deleteJSON(`${endpoint}/${item.id}`); await load(); onChanged?.() },
  })

  const setField = (name, value) => setForm(f => ({ ...f, [name]: value }))

  return (
    <section className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">{title}</h2>
        {subtitle && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-400 dark:text-slate-500 py-6">
          <Loader2 size={16} className="animate-spin" /> {t('common:states.loading')}
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400 dark:text-slate-500 py-4">{t('admin:production.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                {columns.map(c => <th key={c} className="py-2 pr-4 font-medium">{c}</th>)}
                {renderDetail && <th className="py-2 pr-4" />}
                <th className="py-2 w-10" />
              </tr>
            </thead>
            <tbody>
              {items.map(item => {
                const open = renderDetail && openId === item.id
                return (
                  <Fragment key={item.id}>
                    <tr className={`border-b border-slate-100 dark:border-slate-800 ${open ? '' : 'last:border-0'}`}>
                      {toRow(item).map((cell, i) => <td key={i} className="py-2 pr-4 text-slate-700 dark:text-slate-200 align-middle">{cell}</td>)}
                      {renderDetail && (
                        <td className="py-2 pr-4 text-right whitespace-nowrap">
                          <button type="button" onClick={() => setOpenId(open ? null : item.id)} aria-expanded={!!open}
                            className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-brand-600 dark:hover:text-brand-300">
                            {detailToggle(item)}
                            <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
                          </button>
                        </td>
                      )}
                      <td className="py-2 text-right">
                        <button disabled={!mayDelete} onClick={() => remove(item)} title={!mayDelete ? t(reasonDelete) : t('common:actions.delete')} className="text-slate-300 dark:text-slate-600 hover:text-status-down disabled:opacity-40 disabled:cursor-not-allowed">
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                    {open && (
                      <tr className="border-b border-slate-100 dark:border-slate-800 last:border-0">
                        <td colSpan={columns.length + 2}>
                          {renderDetail(item, async () => { await load({ quiet: true }); onChanged?.() })}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <form onSubmit={add} className="mt-4 flex flex-wrap items-end gap-2 pt-4 border-t border-slate-100 dark:border-slate-800">
        {fields.map(f => (
          <label key={f.name} className="flex flex-col gap-1" style={{ minWidth: f.width || '8rem' }}>
            <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">{f.label}</span>
            {f.type === 'select' ? (
              <select className={inputCls} value={form[f.name]} onChange={e => setField(f.name, e.target.value)}>
                {f.placeholder && <option value="">{f.placeholder}</option>}
                {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <input
                className={inputCls}
                type={f.type || 'text'}
                value={form[f.name]}
                required={f.required}
                placeholder={f.placeholder}
                min={f.min}
                max={f.max}
                step={f.step}
                onChange={e => setField(f.name, f.upper ? e.target.value.toUpperCase() : e.target.value)}
              />
            )}
          </label>
        ))}
        <button type="submit" disabled={saving} className={primaryBtn}>
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {t('common:actions.add')}
        </button>
      </form>

      {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
      {confirmDialog}
    </section>
  )
}

export default function AdminProduction() {
  const { t } = useTranslation()
  const hh = (h) => (h === null || h === undefined || h === '' ? '—' : `${String(h).padStart(2, '0')}h`)

  const articleKinds = [
    { value: 'ore', label: t('admin:production.kinds.ore') },
    { value: 'waste', label: t('admin:production.kinds.waste') },
  ]
  const stopBuckets = [
    { value: 'delay', label: t('admin:production.buckets.delay') },
    { value: 'non_productive', label: t('admin:production.buckets.non_productive') },
    { value: 'standby', label: t('admin:production.buckets.standby') },
    { value: 'downtime', label: t('admin:production.buckets.downtime') },
  ]

  return (
    <div className="h-full overflow-y-auto scrollbar-thin p-6 space-y-8">
      <AdminMaterialCatalog />

      <CrudSection
        title={t('admin:production.materials.title')}
        subtitle={t('admin:production.materials.subtitle')}
        endpoint="/api/articles"
        columns={[t('admin:production.cols.code'), t('admin:production.cols.description'), t('admin:production.cols.kind'), t('admin:production.cols.density')]}
        toRow={(a) => [
          <span className="font-medium text-slate-800 dark:text-slate-100">{a.code || '—'}</span>,
          a.description || '—',
          a.kind === 'waste'
            ? <Chip label={t('admin:production.kinds.waste')} color="#f59e0b" />
            : <Chip label={t('admin:production.kinds.ore')} color="#017f96" />,
          <span className="tabular-nums">{a.density ?? '—'}</span>,
        ]}
        fields={[
          { name: 'code', label: t('admin:production.cols.code'), required: true, upper: true, placeholder: 'FE-RUN', width: '8rem' },
          { name: 'description', label: t('admin:production.cols.description'), placeholder: t('admin:production.fields.description_ph'), width: '12rem' },
          { name: 'kind', label: t('admin:production.cols.kind'), type: 'select', default: 'ore', options: articleKinds, width: '8rem' },
          { name: 'density', label: t('admin:production.cols.density'), type: 'number', step: '0.01', min: '0', placeholder: t('admin:production.fields.density_ph'), width: '7rem' },
        ]}
        buildBody={(f) => ({
          code: f.code.trim(),
          description: f.description.trim(),
          kind: f.kind || 'ore',
          density: f.density === '' ? null : Number(f.density),
        })}
        validate={(f) => f.code.trim() !== ''}
      />

      <CrudSection
        title={t('admin:production.shifts.title')}
        subtitle={t('admin:production.shifts.subtitle')}
        endpoint="/api/shifts"
        columns={[t('admin:production.cols.code'), t('admin:production.cols.name'), t('admin:production.cols.begin'), t('admin:production.cols.end')]}
        toRow={(s) => [
          <span className="font-medium text-slate-800 dark:text-slate-100">{s.code || '—'}</span>,
          s.label || '—',
          <span className="tabular-nums">{hh(s.begin_hour)}</span>,
          <span className="tabular-nums">{hh(s.end_hour)}</span>,
        ]}
        fields={[
          { name: 'code', label: t('admin:production.cols.code'), required: true, upper: true, placeholder: 'T1', width: '7rem' },
          { name: 'label', label: t('admin:production.cols.name'), placeholder: t('admin:production.fields.shift_name_ph'), width: '11rem' },
          { name: 'begin_hour', label: t('admin:production.fields.begin_hour'), type: 'number', min: '0', max: '23', placeholder: '7', width: '7rem' },
          { name: 'end_hour', label: t('admin:production.fields.end_hour'), type: 'number', min: '0', max: '23', placeholder: '15', width: '7rem' },
        ]}
        buildBody={(f) => ({
          code: f.code.trim(),
          label: f.label.trim(),
          begin_hour: f.begin_hour === '' ? null : Number(f.begin_hour),
          end_hour: f.end_hour === '' ? null : Number(f.end_hour),
        })}
        validate={(f) => f.code.trim() !== ''}
      />

      <CrudSection
        title={t('admin:production.plan.title')}
        subtitle={t('admin:production.plan.subtitle')}
        endpoint="/api/production-plan"
        columns={[t('admin:production.cols.date'), t('admin:production.cols.indicator'), t('admin:production.cols.article'), t('admin:production.cols.value')]}
        toRow={(p) => [
          <span className="tabular-nums">{p.date || '—'}</span>,
          <span className="font-medium text-slate-800 dark:text-slate-100">{p.code || '—'}</span>,
          p.article || '—',
          <span className="tabular-nums">{p.value ?? '—'}</span>,
        ]}
        fields={[
          { name: 'date', label: t('admin:production.cols.date'), type: 'date', required: true, width: '10rem' },
          { name: 'code', label: t('admin:production.cols.indicator'), type: 'select', default: PLAN_CODES[0], options: PLAN_CODES.map(c => ({ value: c, label: c })), width: '11rem' },
          { name: 'article', label: t('admin:production.cols.article'), placeholder: t('admin:production.fields.article_ph'), width: '9rem' },
          { name: 'value', label: t('admin:production.cols.value'), type: 'number', step: '0.01', placeholder: '0', width: '8rem' },
        ]}
        buildBody={(f) => ({
          date: f.date,
          code: f.code,
          article: f.article.trim() || null,
          mine_id: null,
          value: f.value === '' ? null : Number(f.value),
        })}
        validate={(f) => f.date !== ''}
      />

      <CrudSection
        title={t('admin:production.stops.title')}
        subtitle={t('admin:production.stops.subtitle')}
        endpoint="/api/stop-reasons"
        columns={[t('admin:production.cols.code'), t('admin:production.cols.label'), t('admin:production.cols.bucket')]}
        toRow={(r) => [
          <span className="font-medium text-slate-800 dark:text-slate-100">{r.code || '—'}</span>,
          r.label || '—',
          r.bucket === 'standby'
            ? <Chip label={t('admin:production.buckets.standby')} color="#f79009" />
            : r.bucket === 'downtime'
              ? <Chip label={t('admin:production.buckets.downtime')} color="#f04438" />
              : r.bucket === 'non_productive'
                ? <Chip label={t('admin:production.buckets.non_productive')} color="#dd2590" />
                : <Chip label={t('admin:production.buckets.delay')} color="#017f96" />,
        ]}
        fields={[
          { name: 'code', label: t('admin:production.cols.code'), required: true, upper: true, placeholder: 'STB-LUNCH', width: '9rem' },
          { name: 'label', label: t('admin:production.cols.label'), placeholder: t('admin:production.fields.stop_label_ph'), width: '12rem' },
          { name: 'bucket', label: t('admin:production.cols.bucket'), type: 'select', default: 'delay', options: stopBuckets, width: '9rem' },
        ]}
        buildBody={(f) => ({
          code: f.code.trim(),
          label: f.label.trim(),
          bucket: f.bucket || 'delay',
        })}
        validate={(f) => f.code.trim() !== ''}
        detailToggle={(r) => (
          <>
            <Languages size={13} /> {t('admin:production.stops.translations')}
            <span className="tabular-nums text-slate-400 dark:text-slate-500">{translatedCount(r.labels)}/{TEXT_LANGS.length}</span>
          </>
        )}
        renderDetail={(r, reload) => <StopReasonLabels reason={r} onSaved={reload} />}
        onChanged={() => stopReasonsCache.load({ force: true }).catch(() => {})}
      />
    </div>
  )
}
